/**
 * MarketplaceViewToggle.jsx — public MP/Retail/All switch (iter256).
 *
 * Rendered on catalogues where the shop's `mode` is "marketplace" or
 * "hybrid" (currently the master Marketplace shop at
 * hustlemarketplace.au). Reads/writes a `view` query param so
 * bookmark / share / back-button preserve the shopper's choice.
 *
 * Value can be:
 *   • "mp"     — Marketplace-only items
 *   • "retail" — Retail-only items
 *   • "all"    — Everything (default)
 *
 * The parent page consumes `view` to filter its product list. Vendor-
 * owned shops (mode="retail") never render this toggle — their
 * catalogue is retail-only by product-rule from hp_vendors.py.
 */
import { Globe, ShoppingBag, LayoutGrid } from "lucide-react";

const OPTIONS = [
  { id: "mp",     label: "Marketplace",   icon: Globe,       hint: "MP-only items" },
  { id: "retail", label: "Retail",        icon: ShoppingBag, hint: "Retail-only items" },
  { id: "all",    label: "All",           icon: LayoutGrid,  hint: "Show everything" },
];

export default function MarketplaceViewToggle({
  value = "all",
  onChange,
  testidPrefix = "mp-view",
  className = "",
}) {
  return (
    <div
      data-testid={`${testidPrefix}-group`}
      className={`inline-flex items-center gap-1 p-1 rounded-full bg-white/70 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-700 shadow-sm backdrop-blur ${className}`}
      role="tablist"
      aria-label="Choose which items to show"
    >
      {OPTIONS.map((o) => {
        const Icon = o.icon;
        const active = value === o.id;
        return (
          <button
            key={o.id}
            type="button"
            role="tab"
            aria-selected={active}
            title={o.hint}
            data-testid={`${testidPrefix}-${o.id}`}
            onClick={() => onChange?.(o.id)}
            className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-[13px] font-bold transition ${
              active
                ? "bg-emerald-500 text-slate-950 shadow"
                : "text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800"
            }`}
          >
            <Icon size={14} aria-hidden />
            {o.label}
          </button>
        );
      })}
    </div>
  );
}
